import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useCameras } from "@src/features/cameras";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { AppDrawer } from "./index.native";

type SiteWideDrawerContextValue = {
  openDrawer: () => void;
};

const SiteWideDrawerContext = createContext<SiteWideDrawerContextValue | null>(null);

export const useSiteWideDrawer = (): SiteWideDrawerContextValue => {
  const ctx = useContext(SiteWideDrawerContext);
  if (!ctx) {
    return { openDrawer: () => {} };
  }
  return ctx;
};

type SiteWideDrawerProviderProps = {
  children: ReactNode;
};

export const SiteWideDrawerProvider = ({ children }: SiteWideDrawerProviderProps) => {
  const [visible, setVisible] = useState(false);
  const { data: cameras } = useCameras();
  const selectedSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);
  const siteNames = useSiteNames();

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site", siteNames);
  }, [cameras, siteNames]);

  const openDrawer = useCallback(() => {
    setVisible(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setVisible(false);
  }, []);

  const value = useMemo(() => ({ openDrawer }), [openDrawer]);

  useEffect(() => {
    if (!selectedSiteId || !cameras) return;
    const siteExists = siteGroups.some((g) => g.siteId === selectedSiteId);
    if (!siteExists) {
      setSelectedSiteId(null);
    }
  }, [selectedSiteId, cameras, siteGroups, setSelectedSiteId]);

  return (
    <SiteWideDrawerContext.Provider value={value}>
      {children}
      <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS}>
        <AppDrawer
          visible={visible}
          onClose={closeDrawer}
          selectedSiteId={selectedSiteId}
          onSiteSelect={setSelectedSiteId}
          siteGroups={siteGroups}
        />
      </FeatureGate>
    </SiteWideDrawerContext.Provider>
  );
};
